/**
 * Firebase Auth Emulator Setup and Test Users
 * 
 * This module provides utilities for connecting to the Firebase Auth Emulator
 * and managing test users with different roles.
 * 
 * Usage:
 * 1. Start the emulator: npm run emulators
 * 2. Initialize: initializeAuthEmulator()
 * 3. Sign in test user: signInTestUser('admin')
 * 
 * Available test users:
 * - admin: Administrator role
 * - practitioner: Practitioner role
 * - client: Client role
 */

import { Auth, connectAuthEmulator, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { Firestore, collection, doc, setDoc, getDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { UserRole } from '@/components/authnz/types/rbac';
import { getLocalhostHostname, getLocalhostIP } from '@/config/environment-config';

/**
 * Test user credentials and configurations
 */
export interface TestUser {
  email: string;
  password: string;
  displayName: string;
  role: UserRole;
}
import logger from '@/lib/logging';

/**
 * Available test users with different roles
 */
export const TEST_USERS: Record<string, TestUser> = {
  admin: {
    email: 'admin.test@example.com',
    password: 'Test123!@#Admin',
    displayName: 'Admin Test User',
    role: UserRole.ADMINISTRATOR,
  },
  practitioner: {
    email: 'practitioner.test@example.com',
    password: 'Test123!@#Practitioner',
    displayName: 'Practitioner Test User',
    role: UserRole.PRACTITIONER,
  },
  client: {
    email: 'client.test@example.com',
    password: 'Test123!@#Client',
    displayName: 'Client Test User',
    role: UserRole.CLIENT,
  },
  tutor: {
    email: 'tutor.test@example.com',
    password: 'Test123!@#Tutor',
    displayName: 'Tutor Test User',
    role: UserRole.TUTOR,
  },
  sales: {
    email: 'sales.test@example.com',
    password: 'Test123!@#Sales',
    displayName: 'Sales Test User',
    role: UserRole.SALES,
  },
  practitionerAdmin: {
    email: 'practitioner-admin.test@example.com',
    password: 'Test123!@#PractitionerAdmin',
    displayName: 'Practitioner Administrator Test User',
    role: UserRole.PRACTITIONER_ADMINISTRATOR,
  },
};

/**
 * Check if the Auth emulator is available
 * @returns true if emulator is running
 */
export function isAuthEmulatorRunning(): boolean {
  // The emulator is running on port 9099 (default)
  // We check if we're in a development environment and on localhost
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const localhostHostname = getLocalhostHostname();
    const localhostIP = getLocalhostIP();
    return (
      (hostname === localhostHostname || hostname === localhostIP) &&
      process.env.NODE_ENV === 'development'
    );
  }
  return process.env.NODE_ENV === 'development';
}

/**
 * Initialize the Firebase Auth Emulator connection
 * 
 * This should be called early in the app initialization, before any auth operations.
 * It will only connect if running on localhost in development mode.
 * 
 * @param auth - Firebase Auth instance
 * @param emulatorUrl - Emulator URL (default: http://127.0.0.1:9099)
 * @returns true if emulator was connected, false otherwise
 */
export function initializeAuthEmulator(
  auth: Auth,
  emulatorUrl: string = `http://${getLocalhostIP()}:9099`
): boolean {
  try {
    // Only connect to emulator on localhost in development
    if (!isAuthEmulatorRunning()) {
      logger.debug(`[🔐 Auth Emulator] ℹ️ Not running on ${getLocalhostHostname()} or not in development mode`);
      return false;
    }

    // Check if already connected to prevent double connection
    if ((auth as any).emulatorConfig) {
      logger.debug('[🔐 Auth Emulator] ℹ️ Already connected to Auth Emulator');
      return true;
    }

    connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true });
    logger.debug(`[🔐 Auth Emulator] ✅ Connected to Auth Emulator at ${emulatorUrl}`);
    return true;
  } catch (error) {
    logger.warn('[🔐 Auth Emulator] ⚠️ Could not connect to Auth Emulator:', error instanceof Error ? error.message : String(error));
    return false;
  }
}

/**
 * Create or get a test user with the specified role assignment
 * 
 * This function will:
 * 1. Create the test user in Firebase Auth (if not already created)
 * 2. Create a user record in Firestore
 * 3. Assign the user to the specified role
 * 
 * @param auth - Firebase Auth instance
 * @param db - Firestore instance
 * @param userKey - Key from TEST_USERS ('admin', 'practitioner', 'client', 'tutor')
 * @returns Object with user data or error information
 */
export async function setupTestUser(
  auth: Auth,
  db: Firestore,
  userKey: keyof typeof TEST_USERS
): Promise<{
  success: boolean;
  uid?: string;
  email?: string;
  role?: UserRole;
  error?: string;
}> {
  const testUser = TEST_USERS[userKey];

  if (!testUser) {
    return {
      success: false,
      error: `Unknown test user: ${userKey}. Available: ${Object.keys(TEST_USERS).join(', ')}`,
    };
  }

  try {
    // Try to sign in first (user might already exist)
    try {
      const userCredential = await signInWithEmailAndPassword(auth, testUser.email, testUser.password);
      const uid = userCredential.user.uid;

      // Verify role assignment exists, create if needed
      const userRoleRef = doc(db, 'users', uid, 'roles', testUser.role);
      const userRoleDoc = await getDoc(userRoleRef);

      if (!userRoleDoc.exists()) {
        logger.debug(`[🔐 Auth Emulator] Creating role assignment for ${testUser.email}`);
        await setDoc(userRoleRef, {
          userId: uid,
          email: testUser.email,
          role: testUser.role,
          assignedBy: 'system',
          assignedAt: new Date(),
          updatedAt: new Date(),
        });

        // Also update user document (profile info only, no roles mirroring)
        const userRef = doc(db, 'users', uid);
        await setDoc(userRef, {
          uid,
          email: testUser.email,
          displayName: testUser.displayName,
        }, { merge: true });
      }

      logger.debug(`[🔐 Auth Emulator] ✅ Test user setup successful: ${testUser.email} (${testUser.role})`);
      return {
        success: true,
        uid,
        email: testUser.email,
        role: testUser.role,
      };
    } catch (signInError: any) {
      // User doesn't exist yet, which is expected in emulator
      if (signInError.code !== 'auth/user-not-found') {
        throw signInError;
      }

      // For emulator environment, log that user creation via API isn't available
      // The user needs to be created via Firebase console or emulator UI
      logger.debug(
        `[🔐 Auth Emulator] ℹ️ User ${testUser.email} doesn't exist yet.\n` +
        `Create the user in Firebase Emulator UI at: http://localhost:4000/auth\n` +
        `Email: ${testUser.email}\n` +
        `Password: ${testUser.password}`
      );

      return {
        success: false,
        error: `User not found. Create it in Firebase Emulator UI at http://localhost:4000/auth`,
      };
    }
  } catch (error) {
    logger.error(`[🔐 Auth Emulator] ❌ Error setting up test user ${userKey}:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Sign in a test user
 * 
 * @param auth - Firebase Auth instance
 * @param userKey - Key from TEST_USERS ('admin', 'practitioner', 'client', 'tutor')
 * @returns Object with user UID or error information
 */
export async function signInTestUser(
  auth: Auth,
  userKey: keyof typeof TEST_USERS
): Promise<{ success: boolean; uid?: string; error?: string }> {
  const testUser = TEST_USERS[userKey];

  if (!testUser) {
    return {
      success: false,
      error: `Unknown test user: ${userKey}. Available: ${Object.keys(TEST_USERS).join(', ')}`,
    };
  }

  try {
    const userCredential = await signInWithEmailAndPassword(auth, testUser.email, testUser.password);
    logger.debug(`[🔐 Auth Emulator] ✅ Signed in as ${testUser.email} (${testUser.role})`);
    return {
      success: true,
      uid: userCredential.user.uid,
    };
  } catch (error) {
    logger.error(`[🔐 Auth Emulator] ❌ Error signing in test user ${userKey}:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Sign out the current user
 * 
 * @param auth - Firebase Auth instance
 */
export async function signOutTestUser(auth: Auth): Promise<void> {
  try {
    await signOut(auth);
    logger.debug('[🔐 Auth Emulator] ✅ Signed out');
  } catch (error) {
    logger.error('[🔐 Auth Emulator] ❌ Error signing out:', error);
    throw error;
  }
}

/**
 * Delete a test user and their role assignments
 * 
 * Note: This deletes Firestore records but Auth user must be deleted separately
 * (Firebase Admin SDK is required for that).
 * 
 * @param db - Firestore instance
 * @param uid - User ID to delete
 * @param role - Role of the user (for cleanup)
 */
export async function deleteTestUser(
  db: Firestore,
  uid: string,
  role: UserRole
): Promise<{ success: boolean; error?: string }> {
  try {
    // Delete role assignment from new structure
    const roleRef = doc(db, 'users', uid, 'roles', role);
    await deleteDoc(roleRef);
    logger.debug(`[🔐 Auth Emulator] ✅ Deleted test user ${uid}`);
    return { success: true };
  } catch (error) {
    logger.error(`[🔐 Auth Emulator] ❌ Error deleting test user ${uid}:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Get all test users configuration
 * Useful for testing and debugging
 * 
 * @returns Array of test users
 */
export function getTestUsersInfo(): Array<TestUser & { key: string }> {
  return Object.entries(TEST_USERS).map(([key, user]) => ({
    key,
    ...user,
  }));
}

/**
 * Setup all test users for comprehensive testing
 * 
 * @param auth - Firebase Auth instance
 * @param db - Firestore instance
 * @returns Array of setup results
 */
export async function setupAllTestUsers(
  auth: Auth,
  db: Firestore
): Promise<
  Array<{
    key: string;
    success: boolean;
    uid?: string;
    email?: string;
    role?: UserRole;
    error?: string;
  }>
> {
  const results = [];

  for (const key of Object.keys(TEST_USERS)) {
    const result = await setupTestUser(auth, db, key as keyof typeof TEST_USERS);
    results.push({ key, ...result });
  }

  return results;
}

/**
 * Check if the current auth instance is connected to the emulator
 * 
 * @param auth - Firebase Auth instance
 * @returns true if connected to emulator
 */
export function isConnectedToEmulator(auth: Auth): boolean {
  return !!(auth as any).emulatorConfig;
}

/**
 * Get emulator connection info for debugging
 * 
 * @param auth - Firebase Auth instance
 * @returns Emulator config or null if not connected
 */
export function getEmulatorConfig(auth: Auth): { host: string; port: number; protocol: string } | null {
  const config = (auth as any).emulatorConfig;
  if (!config) return null;

  return {
    host: config.host,
    port: config.port,
    protocol: config.protocol,
  };
}

